"use client";

import { useEffect, useRef } from "react";

type HamburgerSmilIconProps = {
  open: boolean;
  className?: string;
};

const TOP_FRAMES = ["M3 6 L21 6", "M3 12 L21 12", "M6 6 L18 18"];

const MID_FRAMES = ["M3 12 L21 12", "M3 12 L21 12", "M12 12 L12 12"];

const BOTTOM_FRAMES = ["M3 18 L21 18", "M3 12 L21 12", "M6 18 L18 6"];

const MID_OPACITY_FRAMES = ["1", "1", "0"];

function asValues(frames: string[], nextOpen: boolean) {
  const list = nextOpen ? frames : [...frames].reverse();
  return list.join(";");
}

export function HamburgerSmilIcon({ open, className }: HamburgerSmilIconProps) {
  const mountedRef = useRef(false);
  const prevOpenRef = useRef(open);

  const topAnimRef = useRef<SVGAnimateElement | null>(null);
  const midAnimRef = useRef<SVGAnimateElement | null>(null);
  const bottomAnimRef = useRef<SVGAnimateElement | null>(null);
  const midOpacityAnimRef = useRef<SVGAnimateElement | null>(null);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      prevOpenRef.current = open;
      return;
    }

    if (prevOpenRef.current === open) return;
    prevOpenRef.current = open;

    const nextOpen = open;

    if (topAnimRef.current) {
      topAnimRef.current.setAttribute("values", asValues(TOP_FRAMES, nextOpen));
      topAnimRef.current.beginElement();
    }
    if (midAnimRef.current) {
      midAnimRef.current.setAttribute("values", asValues(MID_FRAMES, nextOpen));
      midAnimRef.current.beginElement();
    }
    if (bottomAnimRef.current) {
      bottomAnimRef.current.setAttribute(
        "values",
        asValues(BOTTOM_FRAMES, nextOpen)
      );
      bottomAnimRef.current.beginElement();
    }
    if (midOpacityAnimRef.current) {
      midOpacityAnimRef.current.setAttribute(
        "values",
        asValues(MID_OPACITY_FRAMES, nextOpen)
      );
      midOpacityAnimRef.current.beginElement();
    }
  }, [open]);

  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.6}
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
      aria-hidden="true"
    >
      <path d={open ? TOP_FRAMES.at(-1)! : TOP_FRAMES[0]}>
        <animate
          ref={topAnimRef}
          attributeName="d"
          dur="220ms"
          fill="freeze"
          begin="indefinite"
        />
      </path>
      <path
        d={open ? MID_FRAMES.at(-1)! : MID_FRAMES[0]}
        opacity={open ? 0 : 1}
      >
        <animate
          ref={midAnimRef}
          attributeName="d"
          dur="220ms"
          fill="freeze"
          begin="indefinite"
        />
        <animate
          ref={midOpacityAnimRef}
          attributeName="opacity"
          dur="180ms"
          fill="freeze"
          begin="indefinite"
        />
      </path>
      <path d={open ? BOTTOM_FRAMES.at(-1)! : BOTTOM_FRAMES[0]}>
        <animate
          ref={bottomAnimRef}
          attributeName="d"
          dur="220ms"
          fill="freeze"
          begin="indefinite"
        />
      </path>
    </svg>
  );
}
